import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardOutlinedIcon from '@mui/icons-material/KeyboardOutlined';
import { flattenMenuManifest } from '../config/menuManifest';
import { PAGE_HELP_MANIFEST } from '../config/pageHelpManifest';
import {
  buildPagePermissionKey,
  findMenuItemForPath,
} from '../utils/pagePermissions';

const isTypingTarget = (element) => {
  if (!element) return false;
  const tagName = String(element.tagName || '').toLowerCase();
  if (['input', 'textarea', 'select'].includes(tagName)) return true;
  return Boolean(element.isContentEditable);
};

const PageHelpPanel = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const menuItems = useMemo(() => flattenMenuManifest(), []);
  const activeMenuItem = useMemo(
    () => findMenuItemForPath(location.pathname, menuItems),
    [location.pathname, menuItems]
  );

  const helpEntry = useMemo(() => {
    let match = null;
    for (const item of PAGE_HELP_MANIFEST) {
      const prefix = String(item?.routePrefix || '').trim();
      if (!prefix) continue;
      if (location.pathname === prefix || location.pathname.startsWith(`${prefix}/`)) {
        if (!match || prefix.length > String(match.routePrefix || '').length) {
          match = item;
        }
      }
    }
    return match;
  }, [location.pathname]);

  const shouldAutoOpen = useMemo(() => {
    const value = new URLSearchParams(location.search || '').get('pageHelp');
    return value === '1' || value === 'open' || value === 'true';
  }, [location.search]);

  const title = helpEntry?.title || activeMenuItem?.label || 'Page help';
  const description = helpEntry?.description || 'Няма подадена конкретна инструкция за тази страница.';
  const points = Array.isArray(helpEntry?.points) ? helpEntry.points : [];
  const sections = Array.isArray(helpEntry?.sections) ? helpEntry.sections : [];
  const definitions = Array.isArray(helpEntry?.definitions) ? helpEntry.definitions : [];

  const viewPermission = activeMenuItem ? buildPagePermissionKey(activeMenuItem.key, 'view') : '';
  const editPermission = activeMenuItem ? buildPagePermissionKey(activeMenuItem.key, 'edit') : '';

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.defaultPrevented) return;
      if (isTypingTarget(event.target)) return;
      if (event.ctrlKey || event.altKey || event.metaKey) return;
      if (String(event.key || '').toLowerCase() !== 'i') return;
      event.preventDefault();
      setOpen((prev) => !prev);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    if (shouldAutoOpen) {
      setOpen(true);
    }
  }, [shouldAutoOpen]);

  return (
    <>
      <IconButton
        size="small"
        className="platform-header__help-trigger"
        onClick={() => setOpen(true)}
        aria-label="Open page help"
        title="Page help (i)"
      >
        <InfoOutlinedIcon fontSize="small" />
      </IconButton>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        sx={{
          zIndex: (theme) => theme.zIndex.appBar + 2,
        }}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 420 },
            maxWidth: '100%',
            top: 'var(--platform-header-height, 56px)',
            height: 'calc(100% - var(--platform-header-height, 56px))',
            p: 2,
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h6">{title}</Typography>
          <IconButton size="small" onClick={() => setOpen(false)} aria-label="Close help">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {description}
        </Typography>

        {points.length > 0 && (
          <Box component="ul" sx={{ pl: 2.5, mt: 0, mb: 2 }}>
            {points.map((point) => (
              <li key={point} style={{ marginBottom: 8 }}>
                <Typography variant="body2">{point}</Typography>
              </li>
            ))}
          </Box>
        )}

        {sections.map((section) => {
          const sectionPoints = Array.isArray(section?.points) ? section.points : [];
          if (!section?.title || !sectionPoints.length) {
            return null;
          }

          return (
            <Box key={section.title} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {section.title}
              </Typography>
              <Box component="ul" sx={{ pl: 2.5, mt: 0, mb: 0 }}>
                {sectionPoints.map((point) => (
                  <li key={`${section.title}-${point}`} style={{ marginBottom: 8 }}>
                    <Typography variant="body2">{point}</Typography>
                  </li>
                ))}
              </Box>
            </Box>
          );
        })}

        {definitions.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Parameter glossary
            </Typography>
            <Box sx={{ display: 'grid', gap: 1 }}>
              {definitions.map((item) => (
                <Box
                  key={item.term}
                  sx={{
                    p: 1.25,
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'grey.50',
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {item.term}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {item.description}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        <Divider sx={{ my: 1.5 }} />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Access keys for this page
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          {viewPermission ? <Chip size="small" label={`View: ${viewPermission}`} /> : <Chip size="small" label="View: n/a" />}
          {editPermission ? <Chip size="small" label={`Edit: ${editPermission}`} /> : <Chip size="small" label="Edit: n/a" />}
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <KeyboardOutlinedIcon sx={{ fontSize: 14 }} />
          Shortcuts: press <strong style={{ marginLeft: 4 }}>i</strong> to open/close help.
        </Typography>
      </Drawer>
    </>
  );
};

export default PageHelpPanel;
